import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Currency, FiatWalletAccountState } from '__generated__/globalTypes';
import { RadioGroup } from 'atoms/inputs/RadioGroup';
import { Vertical } from 'atoms/layout/flex';
import { BodyM, BodyS } from 'atoms/typography';
import { NeedsValidatedFiatWalletHelper } from 'components/fiatWallet/NeedsValidatedFiatWalletHelper';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useFiatBalance } from 'hooks/wallets/useFiatBalance';
import { EnterCashWalletKYCFlow_Source } from 'protos/events/platform/web/events';

import { SettingsSection } from '../SettingsSection';
import { useUpdateRewardCurrency } from './useUpdateRewardCurrency';

const Group = styled(Vertical)`
  padding-right: var(--double-unit);
  width: 100%;
`;

const messages = defineMessages({
  title: {
    id: 'Settings.rewardCurrency.title',
    defaultMessage: 'Competition rewards',
  },
  field: {
    id: 'Settings.rewardCurrency.field',
    defaultMessage: 'Receive rewards in',
  },
});

export const RewardCurrency = () => {
  const {
    currentUser,
    fiatCurrency: { code },
    walletPreferences: { showEthWallet, showFiatWallet },
  } = useCurrentUserContext();
  const { canDepositAndWithdraw } = useFiatBalance();

  const userRewardCurrency = currentUser?.userSettings?.rewardCurrency;

  const { updateRewardCurrency } = useUpdateRewardCurrency();

  const onChange = (value: Currency) => {
    updateRewardCurrency(value);
  };

  if (!currentUser) return null;

  const rewardCurrency = userRewardCurrency || Currency.ETH;

  const options = [
    {
      value: Currency.FIAT,
      label: (
        <Group>
          <BodyM color="var(--c-white)">{code}</BodyM>
          <BodyS color="var(--c-nd-600)">
            <FormattedMessage
              id="settings.rewardCurrency.cash.helper"
              defaultMessage="Rewards will be added to your Cash Wallet"
            />
          </BodyS>
        </Group>
      ),
      helper: (
        <NeedsValidatedFiatWalletHelper
          canListAndTradeHelper={
            <FormattedMessage
              id="settings.rewardCurrency.cash.kyc.canListAndTrade"
              defaultMessage="To receive cash rewards you must verify your identity by adding a government ID."
            />
          }
          defaultHelper={
            <FormattedMessage
              id="settings.rewardCurrency.cash.kyc.canNotListAndTrade"
              defaultMessage="To receive cash rewards you must activate your Cash Wallet and verify your identity by adding a government ID."
            />
          }
          statusTarget={FiatWalletAccountState.VALIDATED_OWNER}
          withPadding
          source={
            EnterCashWalletKYCFlow_Source.SETTINGS_CASH_COMPETITION_REWARDS_HELPER
          }
        />
      ),
      disabled: !canDepositAndWithdraw,
    },
    {
      value: Currency.ETH,
      label: <BodyM color="var(--c-white)">ETH</BodyM>,
    },
  ];
  if (!showEthWallet || !showFiatWallet) return null;

  return (
    <SettingsSection title={messages.title}>
      <RadioGroup
        withSpacing
        options={options}
        value={rewardCurrency || Currency.ETH}
        name="currency"
        onChange={onChange}
      />
    </SettingsSection>
  );
};
